import { Language } from "@/components/Inputs/LanguagePicker/Language.ts";
import { useIsCroatian } from "@/helpers/translation/useIsCroatian.ts";
import { CancelationPolicy, CancelationPolicyTranslation } from "@/helpers/types/types.ts";
import { getLocale } from "@/stores/ApolloClient/helpers/getLocale.ts";
import { useTranslation } from "react-i18next";

export interface GetCancelationPolicyTranslatedInfoResponse
  extends Pick<CancelationPolicyTranslation, "name"> {
  description: string;
  translation?: CancelationPolicyTranslation;
  getTranslateCancelationPolicyData: (cp: CancelationPolicy) => {
    translation?: CancelationPolicyTranslation;
    name: string;
    description: string;
  };
}

/**
 * Retrieves translated information for a CancelationPolicy (CP).
 * Default App language is "hr"
 *     - if it's hr return directly the entity values
 *     - if it's not hr, check if there is a translation for the current language and return it,
 *       if not return the entity values
 * @param {CancelationPolicy} cp The CancelationPolicy object to retrieve translated information for.
 * Returns an object containing the translated information
 */
export const useGetCancelationPolicyTranslated = (
  cp?: CancelationPolicy,
): GetCancelationPolicyTranslatedInfoResponse => {
  const { i18n } = useTranslation();

  const isCroatian = useIsCroatian();

  const getTranslateCancelationPolicyData = (cp: CancelationPolicy) => {
    const translation = cp?.translations?.find((t) => t.language === getLocale(i18n.language as Language));
    const enFallback = cp?.translations?.find((t) => t.language === "en");
    const name = isCroatian ? cp?.name : translation?.name || enFallback?.name || cp?.name;
    const description = isCroatian
      ? cp?.description
      : translation?.description || enFallback?.description || cp?.description;
    return { translation, name, description };
  };

  if (cp) {
    return { ...getTranslateCancelationPolicyData(cp), getTranslateCancelationPolicyData };
  }

  return {
    name: "",
    description: "",
    getTranslateCancelationPolicyData,
  };
};
